import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type MouseEvent as ReactMouseEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react'

import styles from '../../components/Canvas/Canvas.module.css'
import type { DotOffset, LetterOffset, Viewport, WordOffset } from '../../types/index.js'
import {
  canvasPresentation,
  dragUpdatesChanged,
  hitTestLayout,
  isLayoutOwner,
  keyboardPanViewport,
  MAX_LAYOUT_OVERLAY_ITEMS,
  offsetDelta,
  panViewport,
  resolveDragUpdates,
  screenToLayoutPoint,
  zoomFromWheel,
  zoomFromKeyboard,
  type DragUpdate,
  type LayoutInteractionMetadata,
} from './interaction.js'
import {
  selectV2CanvasHeight,
  selectV2CanvasWidth,
  type V2Runtime,
} from '../runtime/V2Runtime.js'
import { useEditorSelector } from '../store/useEditorSelector.js'
import { effectiveLayoutFontSize } from '../layout/effectiveFontSize.js'

const selectViewport = (snapshot: ReturnType<V2Runtime['store']['getSnapshot']>) => snapshot.transient.viewport
const selectLayoutSettings = (snapshot: ReturnType<V2Runtime['store']['getSnapshot']>) => snapshot.document.layout
const selectSelection = (snapshot: ReturnType<V2Runtime['store']['getSnapshot']>) => snapshot.transient.selection
const selectTextSettings = (snapshot: ReturnType<V2Runtime['store']['getSnapshot']>) => snapshot.document.text

export interface V2CanvasProps {
  readonly runtime: V2Runtime
  readonly renderingEnabled: boolean
  readonly onResize?: (width: number, height: number) => void
  readonly onViewportChange: (viewport: Partial<Viewport>) => void
  readonly onWordDrag: (wordIndex: number, offset: Partial<WordOffset>) => void
  readonly onWordDragBatch: (updates: Array<{ index: number; offset: Partial<WordOffset> }>) => void
  readonly onWordDragEnd: () => void
  readonly onSelectWord: (index: number | null) => void
  readonly onLetterDrag: (letterIndex: number, offset: Partial<LetterOffset>) => void
  readonly onLetterDragBatch: (updates: Array<{ index: number; offset: Partial<LetterOffset> }>) => void
  readonly onLetterDragEnd: () => void
  readonly onSelectLetter: (index: number | null) => void
  readonly onDotDrag: (dotIndex: number, offset: Partial<DotOffset>) => void
  readonly onDotDragEnd: () => void
  readonly onSelectDot: (index: number | null) => void
}

type DragTarget = 'word' | 'letter' | 'dot'

type ActiveGesture =
  | {
    kind: 'pan'
    pointerId: number
    revision: number
    renderingEnabled: boolean
    letterMode: boolean
    dotMode: boolean
    lastX: number
    lastY: number
    viewport: Viewport
  }
  | {
    kind: 'drag'
    pointerId: number
    revision: number
    renderingEnabled: boolean
    letterMode: boolean
    dotMode: boolean
    target: DragTarget
    index: number
    startX: number
    startY: number
    initialOffsets: ReadonlyArray<Readonly<{ x: number; y: number }>>
    metadata: LayoutInteractionMetadata
    latestUpdates: readonly DragUpdate[]
  }

function requestedDpr(): number {
  if (typeof window === 'undefined') return 1
  const value = window.devicePixelRatio
  return Number.isFinite(value) && value > 0 ? value : 1
}

function currentOffsets(layout: {
    readonly wordOffsets: ReadonlyArray<Readonly<{ x: number; y: number }>>
    readonly letterOffsets: ReadonlyArray<Readonly<{ x: number; y: number }>>
    readonly dotOffsets: ReadonlyArray<Readonly<{ x: number; y: number }>>
  },
  target: DragTarget,
): ReadonlyArray<Readonly<{ x: number; y: number }>> {
  if (target === 'word') return layout.wordOffsets
  if (target === 'letter') return layout.letterOffsets
  return layout.dotOffsets
}

export function V2Canvas({
  runtime,
  renderingEnabled,
  onResize,
  onViewportChange,
  onWordDrag,
  onWordDragBatch,
  onWordDragEnd,
  onSelectWord,
  onLetterDrag,
  onLetterDragBatch,
  onLetterDragEnd,
  onSelectLetter,
  onDotDrag,
  onDotDragEnd,
  onSelectDot,
}: V2CanvasProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const presentationCanvasRef = useRef<HTMLCanvasElement>(null)
  const gestureRef = useRef<ActiveGesture | null>(null)
  const selectedRef = useRef({ word: null as number | null, letter: null as number | null, dot: null as number | null })
  const canvasWidth = useEditorSelector(runtime.store, selectV2CanvasWidth)
  const canvasHeight = useEditorSelector(runtime.store, selectV2CanvasHeight)
  const viewport = useEditorSelector(runtime.store, selectViewport)
  const layoutSettings = useEditorSelector(runtime.store, selectLayoutSettings)
  const selection = useEditorSelector(runtime.store, selectSelection)
  const textSettings = useEditorSelector(runtime.store, selectTextSettings)
  const canvasSnapshot = useSyncExternalStore(
    runtime.subscribeCanvas,
    runtime.getCanvasSnapshot,
    runtime.getCanvasSnapshot,
  )
  const layoutSnapshot = useSyncExternalStore(
    runtime.subscribeLayout,
    runtime.getLayoutSnapshot,
    runtime.getLayoutSnapshot,
  )
  selectedRef.current.word = selection.wordIndex
  selectedRef.current.letter = selection.letterIndex
  selectedRef.current.dot = selection.dotIndex

  useLayoutEffect(() => {
    const canvas = presentationCanvasRef.current
    if (canvas === null) return
    return runtime.attachPresentationCanvas(canvas)
  }, [runtime])

  useLayoutEffect(() => {
    runtime.setSurface(canvasWidth, canvasHeight, requestedDpr())
    onResize?.(canvasWidth, canvasHeight)
  }, [runtime, canvasWidth, canvasHeight, onResize])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const updateSurface = () => {
      runtime.setSurface(canvasWidth, canvasHeight, requestedDpr())
    }
    window.addEventListener('resize', updateSurface, { passive: true })
    return () => { window.removeEventListener('resize', updateSurface) }
  }, [runtime, canvasWidth, canvasHeight])

  const presentation = useCallback(() => {
    const host = hostRef.current
    if (host === null) return null
    const bounds = host.getBoundingClientRect()
    if (bounds.width <= 0 || bounds.height <= 0) return null
    return canvasPresentation(bounds, canvasWidth, canvasHeight)
  }, [canvasWidth, canvasHeight])

  // Track the letterboxed presentation rect (host-relative) so the project
  // canvas bounds stay visible as a rectangle in both render and layout
  // modes, no matter how the host is resized or which aspect the project
  // canvas uses. canvasPresentation reports client coordinates, so the host
  // origin is subtracted for the absolutely positioned overlay.
  const [fitRect, setFitRect] = useState<{ left: number; top: number; width: number; height: number } | null>(null)
  useLayoutEffect(() => {
    const host = hostRef.current
    if (host === null) return
    const update = () => {
      const bounds = host.getBoundingClientRect()
      if (bounds.width <= 0 || bounds.height <= 0 || canvasWidth <= 0 || canvasHeight <= 0) {
        setFitRect(null)
        return
      }
      const fitted = canvasPresentation(bounds, canvasWidth, canvasHeight)
      setFitRect({
        left: fitted.left - bounds.left,
        top: fitted.top - bounds.top,
        width: fitted.width,
        height: fitted.height,
      })
    }
    update()
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update, { passive: true })
      return () => { window.removeEventListener('resize', update) }
    }
    const observer = new ResizeObserver(update)
    observer.observe(host)
    return () => { observer.disconnect() }
  }, [canvasWidth, canvasHeight])

  const selectOnly = useCallback((target: DragTarget | null, index: number | null) => {
    const word = target === 'word' ? index : null
    const letter = target === 'letter' ? index : null
    const dot = target === 'dot' ? index : null
    selectedRef.current = { word, letter, dot }
    onSelectWord(word)
    onSelectLetter(letter)
    onSelectDot(dot)
  }, [onSelectWord, onSelectLetter, onSelectDot])

  const cancelGesture = useCallback((pointerId?: number) => {
    const gesture = gestureRef.current
    if (gesture === null || (pointerId !== undefined && gesture.pointerId !== pointerId)) return false
    gestureRef.current = null
    if (gesture.kind === 'drag') runtime.cancelLayoutEdit(gesture.revision)
    const host = hostRef.current
    if (host !== null) {
      host.style.cursor = 'default'
      if (host.hasPointerCapture(gesture.pointerId)) {
        try { host.releasePointerCapture(gesture.pointerId) } catch { /* Capture may already be gone. */ }
      }
    }
    return true
  }, [runtime])

  useEffect(() => {
    const gesture = gestureRef.current
    if (gesture === null) return
    if (
      gesture.revision !== layoutSnapshot.revision
      || gesture.renderingEnabled !== renderingEnabled
      || gesture.letterMode !== layoutSettings.letterMode
      || gesture.dotMode !== layoutSettings.dotMode
    ) cancelGesture()
  }, [cancelGesture, layoutSettings.dotMode, layoutSettings.letterMode, layoutSnapshot.revision, renderingEnabled])

  useEffect(() => () => {
    const gesture = gestureRef.current
    if (gesture?.kind === 'drag') runtime.cancelLayoutEdit(gesture.revision)
    gestureRef.current = null
  }, [runtime])

  const handleDoubleClick = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    if (renderingEnabled) return
    const fitted = presentation()
    if (fitted === null) return
    const point = screenToLayoutPoint(event.clientX, event.clientY, fitted, viewport)
    const hit = hitTestLayout(layoutSnapshot, point.x, point.y, {
      letterMode: layoutSettings.letterMode,
      dotMode: layoutSettings.dotMode,
      wordOffsets: layoutSettings.wordOffsets,
      letterOffsets: layoutSettings.letterOffsets,
      dotOffsets: layoutSettings.dotOffsets,
      acceptedWordOffsets: layoutSnapshot.offsets.wordOffsets,
      acceptedLetterOffsets: layoutSnapshot.offsets.letterOffsets,
      acceptedDotOffsets: layoutSnapshot.offsets.dotOffsets,
    })
    if (hit === null) selectOnly(null, null)
    else selectOnly(hit.kind, hit.index)
  }, [layoutSettings, layoutSnapshot, presentation, renderingEnabled, selectOnly, viewport])

  const handlePointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (gestureRef.current !== null) return
    const fitted = presentation()
    if (fitted === null) return
    let target: DragTarget | null = null
    let index: number | null = null
    if (event.button === 0 && !renderingEnabled) {
      if (layoutSettings.dotMode && selectedRef.current.dot !== null) {
        target = 'dot'
        index = selectedRef.current.dot
      } else if (layoutSettings.letterMode && selectedRef.current.letter !== null) {
        target = 'letter'
        index = selectedRef.current.letter
      } else if (!layoutSettings.letterMode && selectedRef.current.word !== null) {
        target = 'word'
        index = selectedRef.current.word
      }
    }
    if (target !== null && index !== null) {
      if (!isLayoutOwner(layoutSnapshot, target, index) || !runtime.beginLayoutEdit(layoutSnapshot.revision)) {
        selectOnly(null, null)
        target = null
        index = null
      } else {
        gestureRef.current = {
          kind: 'drag',
          pointerId: event.pointerId,
          revision: layoutSnapshot.revision,
          renderingEnabled,
          letterMode: layoutSettings.letterMode,
          dotMode: layoutSettings.dotMode,
          target,
          index,
          startX: event.clientX,
          startY: event.clientY,
          initialOffsets: currentOffsets(layoutSettings, target),
          metadata: layoutSnapshot,
          latestUpdates: [],
        }
      }
    }
    if (gestureRef.current === null && (event.button === 0 || event.button === 1 || event.button === 2)) {
      gestureRef.current = {
        kind: 'pan',
        pointerId: event.pointerId,
        revision: layoutSnapshot.revision,
        renderingEnabled,
        letterMode: layoutSettings.letterMode,
        dotMode: layoutSettings.dotMode,
        lastX: event.clientX,
        lastY: event.clientY,
        viewport: { ...viewport },
      }
    } else if (gestureRef.current === null) return
    event.currentTarget.focus({ preventScroll: true })
    event.currentTarget.setPointerCapture(event.pointerId)
    event.currentTarget.style.cursor = target === null ? 'grabbing' : 'move'
    event.preventDefault()
  }, [layoutSettings, layoutSnapshot, presentation, renderingEnabled, runtime, selectOnly, viewport])

  const handlePointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = gestureRef.current
    if (gesture === null || gesture.pointerId !== event.pointerId) return
    if (
      gesture.revision !== layoutSnapshot.revision
      || gesture.renderingEnabled !== renderingEnabled
      || gesture.letterMode !== layoutSettings.letterMode
      || gesture.dotMode !== layoutSettings.dotMode
    ) {
      cancelGesture(event.pointerId)
      return
    }
    const fitted = presentation()
    if (fitted === null) return
    if (gesture.kind === 'pan') {
      const next = panViewport(
        gesture.viewport,
        event.clientX - gesture.lastX,
        event.clientY - gesture.lastY,
        fitted.scale,
      )
      gesture.lastX = event.clientX
      gesture.lastY = event.clientY
      gesture.viewport = next
      onViewportChange({ x: next.x, y: next.y })
      return
    }
    const currentZoom = viewport.zoom
    const zoom = Number.isFinite(currentZoom) && currentZoom > 0 ? currentZoom : 1
    const deltaX = (event.clientX - gesture.startX) / fitted.scale / zoom
    const deltaY = (event.clientY - gesture.startY) / fitted.scale / zoom
    const updates = resolveDragUpdates(
      gesture.target,
      gesture.index,
      deltaX,
      deltaY,
      gesture.initialOffsets,
      gesture.metadata,
      event.shiftKey,
    )
    const first = updates[0]
    if (first === undefined) return
    const selectedIndex = gesture.target === 'word'
      ? selectedRef.current.word
      : gesture.target === 'letter'
        ? selectedRef.current.letter
        : selectedRef.current.dot
    if (selectedIndex !== gesture.index || !isLayoutOwner(gesture.metadata, gesture.target, gesture.index)) {
      cancelGesture(event.pointerId)
      return
    }
    gesture.latestUpdates = dragUpdatesChanged(gesture.initialOffsets, updates) ? updates : []
    runtime.updateLayoutEditOffsets(gesture.target, updates)
  }, [cancelGesture, layoutSettings.dotMode, layoutSettings.letterMode, layoutSnapshot.revision, onViewportChange, presentation, renderingEnabled, runtime, viewport.zoom])

  const finishGesture = useCallback((pointerId: number) => {
    const gesture = gestureRef.current
    if (gesture === null || gesture.pointerId !== pointerId) return
    if (
      gesture.revision !== layoutSnapshot.revision
      || gesture.renderingEnabled !== renderingEnabled
      || gesture.letterMode !== layoutSettings.letterMode
      || gesture.dotMode !== layoutSettings.dotMode
    ) {
      cancelGesture(pointerId)
      return
    }
    gestureRef.current = null
    if (gesture.kind !== 'drag') return
    const selectedIndex = gesture.target === 'word'
      ? selectedRef.current.word
      : gesture.target === 'letter'
        ? selectedRef.current.letter
        : selectedRef.current.dot
    if (selectedIndex !== gesture.index || !isLayoutOwner(gesture.metadata, gesture.target, gesture.index)) {
      runtime.cancelLayoutEdit(gesture.revision)
      return
    }
    if (gesture.latestUpdates.length === 0) {
      runtime.cancelLayoutEdit(gesture.revision)
      return
    }
    try {
      const updates = gesture.latestUpdates
      const first = updates[0]
      if (gesture.target === 'word') {
        if (updates.length > 1) onWordDragBatch([...updates])
        else if (first !== undefined) onWordDrag(first.index, first.offset)
        onWordDragEnd()
      } else if (gesture.target === 'letter') {
        if (updates.length > 1) onLetterDragBatch([...updates])
        else if (first !== undefined) onLetterDrag(first.index, first.offset)
        onLetterDragEnd()
      } else {
        if (first !== undefined) onDotDrag(first.index, first.offset)
        onDotDragEnd()
      }
    } finally {
      void runtime.commitLayoutEdit(gesture.revision)
    }
  }, [cancelGesture, layoutSettings.dotMode, layoutSettings.letterMode, layoutSnapshot.revision, onDotDrag, onDotDragEnd, onLetterDrag, onLetterDragBatch, onLetterDragEnd, onWordDrag, onWordDragBatch, onWordDragEnd, renderingEnabled, runtime])

  const handlePointerEnd = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    finishGesture(event.pointerId)
    event.currentTarget.style.cursor = 'default'
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }, [finishGesture])

  const handlePointerCancel = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    cancelGesture(event.pointerId)
  }, [cancelGesture])

  const handleWheel = useCallback((event: ReactWheelEvent<HTMLDivElement>) => {
    event.preventDefault()
    onViewportChange({ zoom: zoomFromWheel(viewport.zoom, event.deltaY) })
  }, [onViewportChange, viewport.zoom])

  const handleKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      cancelGesture()
      selectOnly(null, null)
      event.preventDefault()
      return
    }
    const fitted = presentation()
    if (fitted === null) return
    const nextPan = keyboardPanViewport(viewport, event.key, fitted.scale, event.shiftKey)
    if (nextPan !== null) {
      onViewportChange({ x: nextPan.x, y: nextPan.y })
      event.preventDefault()
      return
    }
    if (event.key === '+' || event.key === '=') {
      onViewportChange({ zoom: zoomFromKeyboard(viewport.zoom, 1) })
      event.preventDefault()
    } else if (event.key === '-' || event.key === '_') {
      onViewportChange({ zoom: zoomFromKeyboard(viewport.zoom, -1) })
      event.preventDefault()
    }
  }, [cancelGesture, onViewportChange, presentation, selectOnly, viewport])

  const transform = `matrix(${String(viewport.zoom)} 0 0 ${String(viewport.zoom)} ${String(canvasWidth * 0.5 * (1 - viewport.zoom) + viewport.x * viewport.zoom)} ${String(canvasHeight * 0.5 * (1 - viewport.zoom) + viewport.y * viewport.zoom)})`
  const inverseZoom = 1 / Math.max(1e-6, Math.abs(viewport.zoom))
  // Overlay labels must use the same effective size the layout engine
  // rasterized with, or the outline text disagrees with the boxes.
  const overlayFontSize = effectiveLayoutFontSize(
    textSettings.content,
    textSettings.fontSize,
    canvasWidth,
    canvasHeight,
  )
  const showDots = !renderingEnabled && layoutSettings.dotMode && layoutSnapshot.dots.count <= MAX_LAYOUT_OVERLAY_ITEMS
  const showLetters = !renderingEnabled && !layoutSettings.dotMode && layoutSettings.letterMode
    && layoutSnapshot.letters.length <= MAX_LAYOUT_OVERLAY_ITEMS
  const showWords = !renderingEnabled && !layoutSettings.dotMode && !layoutSettings.letterMode
    && layoutSnapshot.words.length <= MAX_LAYOUT_OVERLAY_ITEMS
  const overlayTruncated = !renderingEnabled && (
    (layoutSettings.dotMode && layoutSnapshot.dots.count > MAX_LAYOUT_OVERLAY_ITEMS)
    || (!layoutSettings.dotMode && layoutSettings.letterMode && layoutSnapshot.letters.length > MAX_LAYOUT_OVERLAY_ITEMS)
    || (!layoutSettings.dotMode && !layoutSettings.letterMode && layoutSnapshot.words.length > MAX_LAYOUT_OVERLAY_ITEMS)
  )
  // Layout mode always uses a clean black stage with white text and light
  // outlines, independent of the project's background, so the editing view
  // stays readable (a white project background made the white overlay text
  // invisible).
  const layoutBackground = '#000'

  return (
    <div
      ref={hostRef}
      className={styles.canvas}
      role="application"
      aria-label="Calligraphy canvas editor"
      aria-keyshortcuts="Escape ArrowUp ArrowDown ArrowLeft ArrowRight + -"
      tabIndex={0}
      data-v2-renderer={canvasSnapshot.kind ?? 'loading'}
      data-v2-layout-editor={renderingEnabled ? 'inactive' : 'active'}
      data-v2-overlay-truncated={overlayTruncated ? 'true' : 'false'}
      data-v2-viewport-x={String(viewport.x)}
      data-v2-viewport-y={String(viewport.y)}
      data-v2-viewport-zoom={String(viewport.zoom)}
      style={{ touchAction: 'none', cursor: 'default' }}
      onContextMenu={(event) => { event.preventDefault() }}
      onDoubleClick={handleDoubleClick}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerCancel}
      onLostPointerCapture={handlePointerCancel}
      onWheel={handleWheel}
      onKeyDown={handleKeyDown}
    >
      <canvas
        ref={presentationCanvasRef}
        width={0}
        height={0}
        aria-hidden="true"
        data-v2-presentation="stable"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          pointerEvents: 'none',
          display: renderingEnabled ? 'block' : 'none',
        }}
      />
      {!renderingEnabled && (
        <svg
          aria-hidden="true"
          viewBox={`0 0 ${String(canvasWidth)} ${String(canvasHeight)}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
        >
          <rect x={0} y={0} width={canvasWidth} height={canvasHeight} fill={layoutBackground} />
          <g transform={transform} fill="rgba(78,145,255,0.12)" stroke="rgba(100,175,255,0.9)" strokeWidth={inverseZoom}>
            {showWords && layoutSnapshot.words.map((word) => {
              const offset = layoutSettings.wordOffsets[word.index] ?? { x: 0, y: 0 }
              return (
                <g key={`word-${String(word.index)}`}>
                  <rect data-v2-overlay-kind="word" data-v2-overlay-index={word.index} data-v2-overlay-selected={selection.wordIndex === word.index ? 'true' : 'false'} x={word.x + offset.x} y={word.y + offset.y} width={word.width} height={word.height} strokeWidth={selection.wordIndex === word.index ? 3 * inverseZoom : undefined} />
                  {/* Anchor at the raster's exact paint position (textAlign 'right' ≙
                      SVG textAnchor 'start' under direction rtl) so the glyphs sit
                      precisely inside their hit box instead of drifting from it. */}
                  <text x={word.anchorX + offset.x} y={word.baselineY + offset.y} textAnchor="start" direction="rtl" fill="#fff" stroke="none" fontFamily={textSettings.activeFont} fontSize={overlayFontSize}>{word.text}</text>
                </g>
              )
            })}
            {showLetters && layoutSnapshot.letters.map((letter) => {
              const wordOffset = layoutSettings.wordOffsets[letter.wordIndex] ?? { x: 0, y: 0 }
              const letterOffset = layoutSettings.letterOffsets[letter.index] ?? { x: 0, y: 0 }
              const x = letter.x + wordOffset.x + letterOffset.x
              const y = letter.y + wordOffset.y + letterOffset.y
              return (
                <g key={`letter-${String(letter.index)}`}>
                  <rect data-v2-overlay-kind="letter" data-v2-overlay-index={letter.index} data-v2-overlay-selected={selection.letterIndex === letter.index ? 'true' : 'false'} x={x} y={y} width={letter.width} height={letter.height} strokeWidth={selection.letterIndex === letter.index ? 3 * inverseZoom : undefined} />
                  <text x={x + letter.width * 0.5} y={letter.baselineY + wordOffset.y + letterOffset.y} textAnchor="middle" direction="rtl" fill="#fff" stroke="none" fontFamily={textSettings.activeFont} fontSize={overlayFontSize}>{letter.text}</text>
                </g>
              )
            })}
            {showDots && Array.from({ length: layoutSnapshot.dots.count }, (_unused, index) => {
              const wordIndex = layoutSnapshot.dots.wordIndex.at(index)
              const letterIndex = layoutSnapshot.dots.letterIndex.at(index)
              const wordDelta = offsetDelta(layoutSettings.wordOffsets, layoutSnapshot.offsets.wordOffsets, wordIndex)
              const letterDelta = offsetDelta(layoutSettings.letterOffsets, layoutSnapshot.offsets.letterOffsets, letterIndex)
              const dotDelta = offsetDelta(layoutSettings.dotOffsets, layoutSnapshot.offsets.dotOffsets, index)
              const x = (layoutSnapshot.dots.x.at(index) ?? 0) + wordDelta.x + letterDelta.x + dotDelta.x
              const y = (layoutSnapshot.dots.y.at(index) ?? 0) + wordDelta.y + letterDelta.y + dotDelta.y
              const width = layoutSnapshot.dots.width.at(index) ?? 0
              const height = layoutSnapshot.dots.height.at(index) ?? 0
              return <rect key={`dot-${String(index)}`} data-v2-overlay-kind="dot" data-v2-overlay-index={index} data-v2-overlay-selected={selection.dotIndex === index ? 'true' : 'false'} x={x - width * 0.5} y={y - height * 0.5} width={width} height={height} strokeWidth={selection.dotIndex === index ? 3 * inverseZoom : undefined} />
            })}
          </g>
        </svg>
      )}
      {fitRect !== null && (
        <div
          aria-hidden="true"
          data-v2-canvas-bounds="true"
          style={{
            position: 'absolute',
            left: fitRect.left,
            top: fitRect.top,
            width: fitRect.width,
            height: fitRect.height,
            boxSizing: 'border-box',
            pointerEvents: 'none',
            // A light border with a dark outer shadow (and vice versa it
            // still reads) keeps the project canvas edge apparent on any
            // project background, dark or light.
            border: '1px solid rgba(255,255,255,0.45)',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.45)',
          }}
        />
      )}
    </div>
  )
}
